import { ref, watch } from 'vue'
import { createApi } from './api'
import { createCallbacks } from './callbacks'
import { dataGet } from '../../helpers/dot'

/**
 * Creates an async function which has a loading state
 * 
 * @param {Function} callback 
 * @param {*} initialValue 
 * @returns {Object<loading: Ref<Boolean>, details: Ref<*>, execute: Function>}
 */
export function createAsyncFunction(callback, initialValue = {}) {
    const loading = ref(false)
    const details = ref(initialValue)

    return {
        loading: loading,
        details: details,

        execute: async function () {
            loading.value = true

            /**
             * Call the given function
             * 
             * arguments is a globally defined parameter which contains all the parameters given to the function
             * @see https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Functions/arguments
             * 
             * spreading it converts from an Arguments object to an array
             */
            details.value = await callback(...arguments)

            loading.value = false

            return details.value
        },

        reset: () => {
            details.value = {}
            loading.value = false
        }
    }
}

/**
 * Creates an api which contains loading states and data
 * 
 * @param {String}                   action                      The action the wordpress ajax request is listening fr
 * @param {String}                   initialMethod               The HTTP method
 * @param {Object}                   config                      The configuration for the api
 * @param {String|null}              config.contentType          The content type for the body, if null it's guessed as the request is executed
 * @param {Object}                   config.parameters           The default request body
 * @param {Function}                 config.onDataRetrieved      The callback to run through the data after the response
 * @param {Function}                 config.preRequestCallback   The callback to run the data through before the request is made
 * @param {String|null}              config.responseKey          The key of the response where the data is stored  
 * @param {Boolean}                  config.hasAPIValidation     Whether the API could return errors in the response from the server
 * @param {Object}                   config.callbacks            A list of default callbacks for the action
 * @param {Function|Array<Function>} config.callbacks.onError    The callback to run on successful responses
 * @param {Function|Array<Function>} config.callbacks.onSuccess  The callback to run on successful responses
 * 
 * @returns {Object} asyncApi
 */
export function createAsyncApi(action, initialMethod = 'GET', config = {}) {

    /**
     * The default configuration to fallback to if parameters aren't defined
     * 
     * @see ./api.js
     */
    const defaultConfig = {
        responseKey: null,
        hasAPIValidation: true,
        onError: false,
        onSuccess: false,
        callbacks: {
            onSuccess: [],
            onError: []
        }
    }

    config = {
        ...defaultConfig,
        ...config
    }

    const responseKey = ref(config.responseKey)
    const hasAPIValidation = ref(config.hasAPIValidation)

    const errors = ref({})

    const callbacks = createCallbacks(config.callbacks)

    /**
     * The api we're interacting with
     */
    const api = createApi(
        action,
        initialMethod,
        {
            ...config,
            /**
             * If we're given a responseKey we should only return the body inside of that
             * 
             * @param {*} data 
             * @returns {*}
             */
            onDataRetrieved: async (original) => {
                let data = config.onDataRetrieved instanceof Function
                    ? await config.onDataRetrieved(original)
                    : { ...original }

                data = responseKey.value !== null
                    ? dataGet(responseKey.value, data)
                    : data

                if (hasAPIValidation.value && data.code !== 200 && data.code !== 302) {
                    if (data.code === 422) {
                        errors.value = data.data.errors
                    }

                    await callbacks.run('onError', data, original, errors)

                    return {} //If we have an error return an empty object as we should assume no data
                }

                await callbacks.run('onSuccess', data, original)

                return data
            },
        }
    )

    const asyncFunction = createAsyncFunction(api.execute)

    return {
        responseKey: responseKey,
        hasAPIValidation: hasAPIValidation,

        loading: asyncFunction.loading,
        details: asyncFunction.details,
        errors: errors,

        api: {
            parameters: api.parameters,
            method: api.method,
            contentType: api.contentType,
            callback: api.callback,
            sentData: api.sentData,
            execute: async () => {
                errors.value = {} //reset errors on request

                callbacks.run('onRequest')

                await asyncFunction.execute()
            },

            reset: () => {
                asyncFunction.reset()
                api.reset()
            },

            beforeRequest: api.beforeRequest,

            /**
             * A list of callbacks to run for each type of request
             */
            callbacks: callbacks,
        }
    }
}

/**
 * Creates an API which caches and returns the result if requested again
 * 
 * @param {String}                   action                      The action the wordpress ajax request is listening fr
 * @param {String}                   initialMethod               The HTTP method
 * @param {Object}                   config                      The configuration for the api
 * @param {String|null}              config.contentType          The content type for the body, if null it's guessed as the request is executed
 * @param {Object}                   config.parameters           The default request body
 * @param {Function}                 config.onDataRetrieved      The callback to run through the data after the response
 * @param {Function}                 config.preRequestCallback   The callback to run the data through before the request is made
 * @param {String|null}              config.responseKey          The key of the response where the data is stored  
 * @param {Boolean}                  config.hasAPIValidation     Whether the API could return errors in the response from the server
 * @param {Object}                   config.callbacks            A list of default callbacks for the action
 * @param {Function|Array<Function>} config.callbacks.onError    The callback to run on successful responses
 * @param {Function|Array<Function>} config.callbacks.onSuccess  The callback to run on successful responses
 * 
 * @returns {Object}
 */
export function createSingularAsyncApi(action, initialMethod = 'GET', config = {}) {

    const retrieved = ref(false)

    const asyncApi = createAsyncApi(
        action,
        initialMethod,
        config
    )

    /**
     * If we modify the parameters the response is no longer valid so we should clear it
     */
    watch(
        asyncApi.api.parameters,
        () => {
            retrieved.value = false
            asyncApi.details.value = {}
        },
        { deep: true }
    )

    return {
        responseKey: asyncApi.responseKey,
        hasAPIValidation: asyncApi.hasAPIValidation,

        loading: asyncApi.loading,
        retrieved: retrieved,
        details: asyncApi.details,
        errors: asyncApi.errors,

        api: {
            parameters: asyncApi.api.parameters,
            method: asyncApi.api.method,
            contentType: asyncApi.api.contentType,
            callback: asyncApi.api.callback,
            execute: async () => {
                if (!retrieved.value) {
                    if (asyncApi.loading.value) {
                        /**
                         * If we're already loading we should wait for the data to be retrieved
                         * once it has been we should continue,
                         * 
                         * we do this by adding and removing a watch for when the data is retrieved
                         */
                        await new Promise(resolve => {
                            const unwatch = watch(
                                retrieved,
                                () => {
                                    unwatch()
                                    resolve(true)
                                }
                            )
                        })
                    } else {
                        await asyncApi.api.execute()
                    }

                    retrieved.value = true
                }

                return asyncApi.details.value
            },

            reset: () => {
                asyncApi.api.reset()
                retrieved.value = false
            },

            /**
             * The callback to run to map the request data
             */
            beforeRequest: asyncApi.api.beforeRequest,

            /**
             * A list of callbacks to run for each type of request
             */
            callbacks: asyncApi.api.callbacks
        }
    }
}